<template>
  <GenericDataTable
    :items="consultationRequests"
    :headers="headers"
    :total-items="totalItems"
    :page="currentPage"
    :items-per-page="itemsPerPage"
    :loading="isLoading"
    :error="isError"
    @update:options="emit('options-update', $event)"
    @refetch="emit('refetch')"
  >
    <template #[`item.createdAt`]="{ item }">
      <span
        :class="[{ 'opacity-60': item.status === 'deleted' }, 'text-sm text-gray-600 text-nowrap']"
        >{{ formatDateTime(item.createdAt) }}</span
      >
    </template>

    <template #[`item.consultationType`]="{ item }">
      <div :class="{ 'opacity-60': item.status === 'deleted' }">
        <ScheduledConsultationTypeChip
          v-if="isScheduledConsultationSummary(item)"
          :consultation-type="item.scheduledType"
          size="small"
          data-testid="consultation-type-chip"
        />

        <DirectConsultationTypeChip
          v-else
          :variant="'outlined'"
          :consultation-type="item.urgency"
          size="small"
          data-testid="consultation-type-chip"
          dark
        />
      </div>
    </template>

    <template #[`item.requestedBy`]="{ item }">
      <div
        :class="[
          { 'opacity-60': item.status === 'deleted' },
          'flex items-center gap-3 text-gray-900'
        ]"
      >
        <div
          v-if="item.createdBy.user.userName !== item.requestedBy.user.userName"
          class="flex flex-col -space-y-1"
        >
          <VTooltip :text="t('Created by')" :location="'top center'" :offset="0">
            <template #activator="{ props }">
              <PersonPreview
                v-bind="props"
                :person="item.createdBy.user"
                :avatar-size="24"
                data-testid="created-by-preview"
              />
            </template>
          </VTooltip>

          <VTooltip :text="t('Executed by')" :location="'top center'" :offset="0">
            <template #activator="{ props }">
              <PersonPreview
                v-bind="props"
                :person="item.requestedBy.user"
                :avatar-size="24"
                data-testid="requested-by-preview"
              />
            </template>
          </VTooltip>
        </div>

        <PersonPreview
          v-else
          :show-tooltip="true"
          :person="item.createdBy.user"
          :avatar-size="40"
          data-testid="created-by-preview-single"
        />
      </div>
    </template>

    <template #[`item.acceptedBy`]="{ item }">
      <div :class="[{ 'opacity-60': item.status === 'deleted' }, 'flex items-center gap-2']">
        <PersonAvatarGroup
          :items="
            item.acceptedBy.map((el: UserDetailsDTO) => ({
              firstName: el.user.firstName ?? '',
              lastName: el.user.lastName ?? '',
              type: 'physician'
            }))
          "
          :size="item.acceptedBy.length > 1 ? 24 : 40"
          data-testid="accepted-group"
        />

        <div v-if="item.acceptedBy.length === 1" class="flex items-center gap-3">
          <p class="text-sm text-gray-900 font-semibold" data-testid="accepted-user-single">
            {{ item.acceptedBy[0].user.firstName ?? '' }}
            {{ item.acceptedBy[0].user.lastName ?? '' }}
          </p>
        </div>
      </div>
    </template>

    <template #[`item.patient`]="{ item }">
      <div :class="[{ 'opacity-60': item.status === 'deleted' }, 'flex items-center gap-2']">
        <UnavailablePatientPreview v-if="item.patients.length === 0" :avatar-size="40" />
        <template v-else>
          <PersonAvatarGroup
            :items="
              item.patients.map((el: PatientDTO) => ({
                firstName: el.firstName ?? '',
                lastName: el.lastName ?? '',
                type: 'patient'
              }))
            "
            :size="item.patients.length > 1 ? 24 : 40"
            data-testid="patients-group"
          />
          <div v-if="item.patients.length === 1" class="flex items-center gap-3">
            <p class="text-sm text-gray-900 font-semibold" data-testid="patient-single">
              {{ item.patients[0].firstName ?? '' }}
              {{ item.patients[0].lastName ?? '' }}
            </p>
          </div>
        </template>
      </div>
    </template>

    <template #[`item.actions`]="{ item }">
      <VChip
        v-if="item.status === 'deleted'"
        :text="t('Deleted on') + ' ' + formatDateTime(item.deletedAt)"
        :variant="'outlined'"
        :density="'compact'"
        class="font-semibold"
        color="error-700 bg-error-50 border-error-200"
      />

      <VMenu
        v-else
        :disabled="!isScheduledConsultationSummary(item)"
        :close-on-content-click="false"
      >
        <template #activator="{ props }">
          <VBtn
            v-bind="props"
            variant="plain"
            class="text-brand-700 font-semibold opacity-100 p-0"
            @click="handleDownloadReportButtonClick(item)"
          >
            {{ t('Download consultation report') }}
          </VBtn>
        </template>

        <VList v-if="item.reports.reportFiles.length > 1">
          <VListItem
            v-for="report in item.reports.reportFiles"
            v-bind:key="report.id"
            class="px-5 py-1 hover:bg-gray-200 [--v-list-prepend-gap:8px]"
          >
            <div class="flex">
              <div class="flex flex-col gap-1">
                <div class="flex justify-between items-center">
                  <PersonPreview v-if="report.patient" :person="report.patient" :avatar-size="32" />
                  <UnavailablePatientPreview v-else :avatar-size="32" />
                  <VTooltip :text="t('Download consultation report')" :location="'bottom center'">
                    <template #activator="{ props }">
                      <VBtn
                        v-bind="props"
                        :icon="'download'"
                        :color="'primary'"
                        :variant="'text'"
                        :density="'compact'"
                        @click="downloadReport(report.id)"
                      />
                    </template>
                  </VTooltip>
                </div>

                <div
                  v-for="physician in item.reports.reportParticipants"
                  :key="physician.user.userName"
                  class="flex items-center gap-1"
                >
                  <p class="text-xs text-gray-900">{{ t('Created by') }}</p>

                  <PersonPreview :person="physician.user" :avatar-size="24" />
                </div>
              </div>
            </div>
          </VListItem>
        </VList>
      </VMenu>
    </template>
  </GenericDataTable>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import DirectConsultationTypeChip from '@/atoms/DirectConsultationTypeChip.vue'
import {
  type DirectConsultationSummaryDTO,
  type PaginatedResponseConsultationSummaryDTOItemsInner,
  type PatientDTO,
  type ScheduledConsultationSummaryDTO,
  type UserDetailsDTO
} from '@/generated'
import ScheduledConsultationTypeChip from '@/atoms/ScheduledConsultationTypeChip.vue'
import { VBtn } from 'vuetify/components'
import PersonPreview from '@/hospitalAdminTool/settings/routingLog/stepCard/PersonPreview.vue'
import { useNoticeStore } from '@/stores/notice'
import { NoticeType } from '@/stores/models/Notice'
import { FileService } from '@/services/telehealthApi/FileService'
import { useFormatDate } from '@/composables/useFormatDate'
import { computed } from 'vue'
import GenericDataTable, {
  type DataTableHeaders,
  type DataTableOptions
} from '@/components/common/GenericDataTable.vue'
import { isScheduledConsultationSummary } from '@/util/isScheduledConsultation'
import PersonAvatarGroup from '@/atoms/PersonAvatarGroup.vue'
import UnavailablePatientPreview from '@/atoms/UnavailablePatientPreview.vue'

interface Props {
  isLoading: boolean
  isError: string | null
  consultationRequests: (DirectConsultationSummaryDTO | ScheduledConsultationSummaryDTO)[]
  totalItems: number
  currentPage: number
  itemsPerPage: number
  displayPatientsColumn?: boolean
}

interface Emits {
  (e: 'refetch'): void
  (e: 'options-update', options: DataTableOptions): void
}

const { t } = useI18n()
const { formatDateTime } = useFormatDate()
const { consultationRequests, displayPatientsColumn = true } = defineProps<Props>()
const emit = defineEmits<Emits>()

const headers = computed<DataTableHeaders>(() => {
  const fixedHeaders = [
    { title: t('Created at'), key: 'createdAt' },
    { title: t('Consultation Type'), key: 'consultationType' },
    { title: t('Requested by'), key: 'requestedBy' },
    { title: t('Accepted by'), key: 'acceptedBy' }
  ]

  const patientHeader = { title: t('Patient'), key: 'patient' }
  const actionsHeader = { title: '', key: 'actions', sortable: false, align: 'end' }

  return [...fixedHeaders, ...(displayPatientsColumn ? [patientHeader] : []), actionsHeader]
})

async function handleDownloadReportButtonClick(
  item: PaginatedResponseConsultationSummaryDTOItemsInner
) {
  const reportFiles = item.reports.reportFiles

  if (!reportFiles.length) {
    useNoticeStore().add({
      type: NoticeType.INFO,
      message: t('No final report exist')
    })
  } else {
    if (reportFiles.length === 1) {
      const report = reportFiles[0]
      await downloadReport(report.id)
    }
  }
}

async function downloadReport(reportId: string) {
  try {
    const { downloadUrl } = await FileService.getDownloadUrl(reportId)
    window.location.href = downloadUrl
  } catch (error) {
    useNoticeStore().add({
      type: NoticeType.ERROR,
      message: t('Failed to download report')
    })
  }
}
</script>
